import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Restaurant,
  LocalLaundryService,
  Spa,
  RoomService,
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  CheckCircle,
  AccessTime,
  LocalDining,
  CleaningServices,
} from '@mui/icons-material';
import ServiceOrderDialog from '../components/ServiceOrderDialog';
import ViewServiceOrderDialog from '../components/ViewServiceOrderDialog';

const mockServiceOrders = [
  {
    id: 'SRV001',
    type: 'Restaurant',
    guest: 'John Doe',
    room: '204',
    items: 'Breakfast Buffet x2, Orange Juice',
    amount: 45,
    status: 'Completed',
    time: '08:30 AM',
    date: '2025-11-16',
  },
  {
    id: 'SRV002',
    type: 'Room Service',
    guest: 'Jane Smith',
    room: '305',
    items: 'Club Sandwich, Coffee',
    amount: 28,
    status: 'In Progress',
    time: '12:15 PM',
    date: '2025-11-16',
  },
  {
    id: 'SRV003',
    type: 'Laundry',
    guest: 'Alice Brown',
    room: '102',
    items: '5 shirts, 3 pants, 2 dresses',
    amount: 35,
    status: 'Pending',
    time: '09:00 AM',
    date: '2025-11-16',
  },
  {
    id: 'SRV004',
    type: 'Spa',
    guest: 'Bob Wilson',
    room: '208',
    items: 'Swedish Massage 60min',
    amount: 120,
    status: 'Confirmed',
    time: '03:00 PM',
    date: '2025-11-16',
  },
  {
    id: 'SRV005',
    type: 'Restaurant',
    guest: 'Carol Davis',
    room: '401',
    items: 'Dinner Set, Wine',
    amount: 85,
    status: 'In Progress',
    time: '07:30 PM',
    date: '2025-11-16',
  },
];

export default function Services() {
  const [activeTab, setActiveTab] = React.useState(0);
  const [orders, setOrders] = React.useState(mockServiceOrders);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [viewDialogOpen, setViewDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('add');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const serviceTypes = ['All', 'Restaurant', 'Room Service', 'Laundry', 'Spa'];

  const filteredOrders = orders.filter((order) => {
    const matchesSearch = order.guest.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.room.includes(searchQuery) ||
      order.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 0 || order.type === serviceTypes[activeTab];
    return matchesSearch && matchesTab;
  });

  const handleMenuClick = (event, order) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleAddOrder = () => {
    setSelectedOrder(null);
    setDialogMode('add');
    setDialogOpen(true);
  };

  const handleEditOrder = () => {
    setDialogMode('edit');
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleViewOrder = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveOrder = (orderData) => {
    if (dialogMode === 'add') {
      setOrders([...orders, orderData]);
      setSnackbar({
        open: true,
        message: `Service order ${orderData.id} created successfully!`,
        severity: 'success',
      });
    } else {
      setOrders(orders.map(o => o.id === orderData.id ? orderData : o));
      setSnackbar({
        open: true,
        message: `Service order ${orderData.id} updated successfully!`,
        severity: 'success',
      });
    }
  };

  const handleDeleteOrder = () => {
    if (selectedOrder) {
      setOrders(orders.filter(o => o.id !== selectedOrder.id));
      setSnackbar({
        open: true,
        message: `Service order ${selectedOrder.id} deleted successfully!`,
        severity: 'success',
      });
      setAnchorEl(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Confirmed': return 'info';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'Restaurant': return <Restaurant />;
      case 'Room Service': return <RoomService />;
      case 'Laundry': return <LocalLaundryService />;
      case 'Spa': return <Spa />;
      default: return <RoomService />;
    }
  };

  const stats = [
    { label: 'Total Orders Today', value: orders.length, color: '#1976d2', icon: RoomService },
    { label: 'Restaurant Orders', value: orders.filter(o => o.type === 'Restaurant').length, color: '#2e7d32', icon: Restaurant },
    { label: 'Laundry Requests', value: orders.filter(o => o.type === 'Laundry').length, color: '#ed6c02', icon: LocalLaundryService },
    { 
      label: 'Total Revenue', 
      value: `$${orders.reduce((sum, o) => sum + o.amount, 0).toLocaleString()}`, 
      color: '#9c27b0',
      icon: LocalDining,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Services & Amenities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage restaurant, room service, laundry, and spa bookings
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddOrder}>
          New Service Order
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            {serviceTypes.map((type, index) => (
              <Tab key={type} label={type} />
            ))}
          </Tabs>
        </Box>
        <Box p={2}>
          <TextField
            fullWidth
            placeholder="Search by guest name, room number, or order ID..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Order ID</strong></TableCell>
              <TableCell><strong>Service Type</strong></TableCell>
              <TableCell><strong>Guest</strong></TableCell>
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Items/Details</strong></TableCell>
              <TableCell><strong>Time</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow key={order.id} hover>
                <TableCell><strong>{order.id}</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                      {getServiceIcon(order.type)}
                    </Avatar>
                    <Typography variant="body2">{order.type}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{order.guest}</TableCell>
                <TableCell><Chip label={order.room} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {order.items}
                  </Typography>
                </TableCell>
                <TableCell>{order.time}</TableCell>
                <TableCell><strong>${order.amount}</strong></TableCell>
                <TableCell>
                  <Chip
                    label={order.status}
                    size="small"
                    color={getStatusColor(order.status)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, order)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleViewOrder}>
          <CheckCircle fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditOrder}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Order
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedOrder) {
            setOrders(orders.map(o => 
              o.id === selectedOrder.id ? { ...o, status: 'Completed' } : o
            ));
            setSnackbar({
              open: true,
              message: `Order ${selectedOrder.id} marked as completed!`,
              severity: 'success',
            });
          }
          setAnchorEl(null);
        }}>
          <CheckCircle fontSize="small" sx={{ mr: 1 }} />
          Mark Completed
        </MenuItem>
        <MenuItem onClick={handleDeleteOrder}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete Order
        </MenuItem>
      </Menu>

      <ServiceOrderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        order={selectedOrder}
        onSave={handleSaveOrder}
        mode={dialogMode}
      />

      <ViewServiceOrderDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        order={selectedOrder}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
